import { useEffect, useRef, useState } from "react";

// Caret SVG used in the nav + Actions triggers (verbatim from getHTML).
function Caret({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="currentColor">
      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function Brand() {
  return (
    <div className="header-brand">
      <img src="https://downloads.atxfilmlab.com/tpfl.png" alt="Turbo Photo Film Lab" className="header-logo" />
      <div className="header-mascots">
        <img src="https://downloads.atxfilmlab.com/favicon.png" alt="" className="header-icon" />
        <img src="https://downloads.atxfilmlab.com/flo.png" alt="" className="header-icon" />
      </div>
    </div>
  );
}

function NavDropdown({ allOrdersHref = "#", allOrdersActive = true }: { allOrdersHref?: string; allOrdersActive?: boolean }) {
  return (
    <div className="app-nav-dropdown">
      <span className="nav-dropdown-trigger">
        <span className="nav-current">All Orders</span>
        <Caret />
      </span>
      <div className="nav-dropdown-menu">
        <div className="nav-dropdown-menu-inner">
          <a href="https://invoices.atxfilmlab.com" className="nav-dropdown-item">
            Invoices
          </a>
          <a href={allOrdersHref} className={`nav-dropdown-item${allOrdersActive ? " active" : ""}`}>
            All Orders
          </a>
          <a href="https://labels.atxfilmlab.com" className="nav-dropdown-item">
            Labels
          </a>
          <a href="https://r2.tpfl.net" className="nav-dropdown-item">
            R2
          </a>
        </div>
      </div>
    </div>
  );
}

export type HeaderProps = {
  variant?: "main" | "detail" | "emails";
  tasksLabel?: string;
  tasksZero?: boolean;
  onOpenActions?: (panel: string) => void;
  onOpenTasks?: () => void;
  onOpenTips?: () => void;
};

export function Header({ variant = "main", tasksLabel = "0", tasksZero = true, onOpenActions, onOpenTasks, onOpenTips }: HeaderProps) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!actionsOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setActionsOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [actionsOpen]);

  const openPanel = (panel: string) => {
    setActionsOpen(false);
    onOpenActions?.(panel);
  };

  return (
    <header className="app-header">
      <div className="header-left">
        <Brand />
        <div className="header-divider" />
        <NavDropdown allOrdersHref={variant === "main" ? "#" : "/"} allOrdersActive={variant !== "emails"} />
      </div>

      <div className="header-right">
        {variant === "main" && (
          <>
            <div className="functions-dropdown-wrapper" ref={wrapRef}>
              <button
                className="functions-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActionsOpen((v) => !v);
                }}
              >
                Actions
                <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                </svg>
              </button>
              <div className={`functions-dropdown-menu${actionsOpen ? " open" : ""}`}>
                <a href="#" className="functions-dropdown-item" onClick={(e) => { e.preventDefault(); openPanel("clubMember"); }}>
                  Manual Club Member
                </a>
                <a href="#" className="functions-dropdown-item" onClick={(e) => { e.preventDefault(); openPanel("scansLink"); }}>
                  Manual Scans Link
                </a>
                <a href="#" className="functions-dropdown-item" onClick={(e) => { e.preventDefault(); openPanel("downloadUrl"); }}>
                  Direct Download URL
                </a>
                <a href="#" className="functions-dropdown-item" onClick={(e) => { e.preventDefault(); openPanel("bugReport"); }}>
                  Bug Report
                </a>
                <a href="#" className="functions-dropdown-item" onClick={(e) => { e.preventDefault(); openPanel("featureRequest"); }}>
                  Feature Request
                </a>
              </div>
            </div>

            <div className="tasks-notification-wrapper">
              <button className="tasks-notification-button" onClick={onOpenTasks}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span
                  className={`tasks-bubble${tasksZero ? " zero" : ""}`}
                  style={tasksZero ? undefined : { minWidth: "auto", padding: "0 6px" }}
                >
                  {tasksLabel}
                </span>
              </button>
            </div>

            <a href="/emails" className="emails-nav-link">
              Email Inbox
            </a>
            <button className="tips-button" onClick={onOpenTips}>
              Tips
            </button>
          </>
        )}

        {variant === "detail" && (
          <button className="tips-button" onClick={onOpenTips}>
            Tips
          </button>
        )}

        {variant === "emails" && (
          <a href="/" className="emails-nav-link">
            &larr; All Orders
          </a>
        )}
      </div>
    </header>
  );
}
